import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { FUEL_COLORS, formatMonth, EmptyState } from './shared';

/**
 * Capacity Factor Heatmap
 *
 * Monthly capacity factor (%) per asset rendered as a grid.
 * Rows = assets, columns = months, color intensity = CF%.
 * Instantly shows seasonal patterns, underperformance, and degradation.
 */

const getCFColor = (pct) => {
  if (pct === 0 || pct == null) return '#f8fafc';
  if (pct < 5) return '#fee2e2';
  if (pct < 10) return '#fecaca';
  if (pct < 20) return '#fde68a';
  if (pct < 30) return '#fcd34d';
  if (pct < 40) return '#bef264';
  if (pct < 50) return '#86efac';
  if (pct < 60) return '#6ee7b7';
  if (pct < 70) return '#34d399';
  if (pct < 80) return '#10b981';
  if (pct < 90) return '#059669';
  return '#047857';
};

const CapacityFactorHeatmap = ({ data, assets }) => {
  if (!data || data.length === 0) return <EmptyState message="No capacity factor data available" />;

  const assetLookup = {};
  (assets || []).forEach((a) => { assetLookup[a.asset_id] = a; });

  const assetIds = [...new Set(data.map((d) => d.asset_id))];
  const months = [...new Set(data.map((d) => d.month))].sort();

  const lookup = {};
  data.forEach(({ asset_id, month, capacity_factor_pct }) => {
    lookup[`${asset_id}_${month}`] = capacity_factor_pct;
  });

  const cellSize = 32;
  const labelWidth = 180;

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ display: 'inline-block', minWidth: labelWidth + months.length * cellSize + 20 }}>
        {/* Month header row */}
        <Box sx={{ display: 'flex', pl: `${labelWidth}px` }}>
          {months.map((m) => (
            <Box key={m} sx={{ width: cellSize, textAlign: 'center', fontSize: '0.62rem', color: 'text.secondary', fontWeight: 600 }}>
              {formatMonth(m)}
            </Box>
          ))}
        </Box>

        {/* Asset rows */}
        {assetIds.map((aid) => {
          const info = assetLookup[aid];
          return (
            <Box key={aid} sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{
                width: labelWidth, fontSize: '0.72rem', color: 'text.primary', fontWeight: 500,
                pr: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                display: 'flex', alignItems: 'center', gap: 0.5,
              }}>
                <Box sx={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  bgcolor: FUEL_COLORS[info?.fuel_code] || FUEL_COLORS.Other,
                }} />
                <span>{info?.name || aid}</span>
              </Box>
              {months.map((m) => {
                const cf = lookup[`${aid}_${m}`];
                return (
                  <Tooltip key={`${aid}_${m}`} title={`${info?.name || aid} — ${formatMonth(m)}: ${cf != null ? cf.toFixed(1) : 'N/A'}% CF`} arrow placement="top">
                    <Box sx={{
                      width: cellSize - 2, height: cellSize - 6, m: '1px', mt: '3px',
                      bgcolor: getCFColor(cf), borderRadius: '3px',
                      border: '1px solid rgba(0,0,0,0.04)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'default', transition: 'transform 0.1s',
                      '&:hover': { transform: 'scale(1.1)', zIndex: 1 },
                    }}>
                      {cf != null && cf > 0 && (
                        <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: cf > 50 ? '#fff' : '#374151' }}>
                          {cf.toFixed(0)}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          );
        })}

        {/* Legend */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2, pl: `${labelWidth}px` }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mr: 1 }}>Capacity Factor:</Typography>
          {[0, 10, 20, 30, 50, 70, 90].map((pct) => (
            <Box key={pct} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Box sx={{ width: 14, height: 14, bgcolor: getCFColor(pct), borderRadius: '2px', border: '1px solid rgba(0,0,0,0.08)' }} />
              <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>{pct}%</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default CapacityFactorHeatmap;
